import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import * as Flags from 'country-flag-icons/react/3x2';
import { CountrySelectStyle } from './CountrySelectCodeStyle';
import { useFormContext } from 'react-hook-form';

const countries = getCountries();
type CountryCode = typeof countries[number];

interface CountryOption {
  value: CountryCode;
  label: string;
  flag: React.ReactNode;
}

const CountrySelect: React.FC<{
  value: CountryOption | null;
  onChange: (country: CountryOption | null) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {

  const countryOptions = countries.map((country) => ({
    value: country,
    label: `+${getCountryCallingCode(country)}`,
    flag: React.createElement(Flags[country as keyof typeof Flags])
  }));

  const customOption = (data: CountryOption) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {data.flag} {data.label}
    </div>
  );

  return (
    <Select
      value={value}
      onChange={onChange}
      options={countryOptions}
      formatOptionLabel={(data: CountryOption) => customOption(data)}
      styles={CountrySelectStyle}
      placeholder="Select Country"
      isDisabled={disabled}
    />
  );
};

interface CountrySelectCodeProps {
  codeClassName?: string;
  onChange: (params: {label: string; countryNameCode: string}) => void;
  disabled?: boolean;  
}

const CountrySelectCode: React.FC<CountrySelectCodeProps> = ({ codeClassName, onChange, disabled}) => {  
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const { setValue } = useFormContext();

  // Function to detect user's country based on some logic
  const detectUserCountry = () => {
    // Replace this logic with your actual implementation to detect user's country
    // For example, you might use geolocation API or IP-based lookup
    return 'GB'; // Defaulting to US for example purposes
  };

  useEffect(() => {
    // Detect the user's country and set it as the default value
    const userCountryCode = detectUserCountry();
    const defaultCountryOption = countries.find(country => country === userCountryCode);

    if (defaultCountryOption) {
      setSelectedCountry({
        value: defaultCountryOption,
        label: `+${getCountryCallingCode(defaultCountryOption)}`,
        flag: React.createElement(Flags[defaultCountryOption as keyof typeof Flags])
      });
    }
  }, []); // Empty dependency array ensures this effect runs only once

  const handleCountryChange = (country: CountryOption | null) => {
    setSelectedCountry(country);
    if (country && country.label) {
      onChange({label: country.label, countryNameCode: country.value});
      setValue("countryCode", country.label);
      setValue('countryNameCode', country.value);
    }
  };

  return (
    <div className={codeClassName}>
      <CountrySelect disabled={disabled}
        value={selectedCountry}
        onChange={handleCountryChange}
      />
    </div>
  );
};

export default CountrySelectCode;
